import { useState, useEffect } from 'react';

// Filter state interface
export interface OnboardingFilterState {
  username: string;
  fullName: string;
  onboardDateFrom: string;
  onboardDateTo: string;
  emailSentDateFrom: string;
  emailSentDateTo: string;
  activeFilters: string[];
}

// Custom hook for onboarding filter management
export const useOnboardingFilter = () => {
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<OnboardingFilterState>({
    username: '',
    fullName: '',
    onboardDateFrom: '',
    onboardDateTo: '',
    emailSentDateFrom: '',
    emailSentDateTo: '',
    activeFilters: []
  });
  
  const [tempState, setTempState] = useState<Omit<OnboardingFilterState, 'activeFilters'>>({
    username: '',
    fullName: '',
    onboardDateFrom: '',
    onboardDateTo: '',
    emailSentDateFrom: '',
    emailSentDateTo: ''
  });
  
  const [expandedAccordions, setExpandedAccordions] = useState<{[key: string]: boolean}>({
    username: true,
    fullName: true,
    onboardDate: true,
    emailSentDate: true
  });

  const handleUsernameChange = (value: string) => {
    setTempState(prev => ({ ...prev, username: value }));
  };

  const handleFullNameChange = (value: string) => {
    setTempState(prev => ({ ...prev, fullName: value }));
  };

  const handleOnboardDateFromChange = (value: string) => {
    setTempState(prev => ({ ...prev, onboardDateFrom: value }));
  };

  const handleOnboardDateToChange = (value: string) => {
    setTempState(prev => ({ ...prev, onboardDateTo: value }));
  };

  const handleEmailSentDateFromChange = (value: string) => {
    setTempState(prev => ({ ...prev, emailSentDateFrom: value }));
  };

  const handleEmailSentDateToChange = (value: string) => {
    setTempState(prev => ({ ...prev, emailSentDateTo: value }));
  };

  const applyFilters = () => {
    const newActiveFilters: string[] = [];
    
    // Helper function to parse date string as local date (not UTC)
    const parseLocalDate = (dateStr: string): Date => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    
    if (tempState.username.trim() !== '') {
      newActiveFilters.push(`Username: ${tempState.username}`);
    }
    
    if (tempState.fullName.trim() !== '') {
      newActiveFilters.push(`Full Name: ${tempState.fullName}`);
    }
    
    if (tempState.onboardDateFrom.trim() !== '') {
      newActiveFilters.push(`Onboard Date From: ${parseLocalDate(tempState.onboardDateFrom).toLocaleDateString()}`);
    }
    
    if (tempState.onboardDateTo.trim() !== '') {
      newActiveFilters.push(`Onboard Date To: ${parseLocalDate(tempState.onboardDateTo).toLocaleDateString()}`);
    }
    
    if (tempState.emailSentDateFrom.trim() !== '') {
      newActiveFilters.push(`Email Sent From: ${parseLocalDate(tempState.emailSentDateFrom).toLocaleDateString()}`);
    }
    
    if (tempState.emailSentDateTo.trim() !== '') {
      newActiveFilters.push(`Email Sent To: ${parseLocalDate(tempState.emailSentDateTo).toLocaleDateString()}`);
    }
    
    setFilterState({
      username: tempState.username.trim(),
      fullName: tempState.fullName.trim(),
      onboardDateFrom: tempState.onboardDateFrom.trim(),
      onboardDateTo: tempState.onboardDateTo.trim(),
      emailSentDateFrom: tempState.emailSentDateFrom.trim(),
      emailSentDateTo: tempState.emailSentDateTo.trim(),
      activeFilters: newActiveFilters
    });
    
    setFilterDrawerOpen(false);
  };
  
  const clearFilters = () => {
    const emptyState = {
      username: '',
      fullName: '',
      onboardDateFrom: '',
      onboardDateTo: '',
      emailSentDateFrom: '',
      emailSentDateTo: ''
    };
    
    setFilterState({
      ...emptyState,
      activeFilters: []
    });
    
    setTempState(emptyState);
  };

  const removeFilter = (filter: string) => {
    const newFilterState = { ...filterState };
    
    if (filter.startsWith('Username:')) {
      newFilterState.username = '';
      setTempState(prev => ({ ...prev, username: '' }));
    } else if (filter.startsWith('Full Name:')) {
      newFilterState.fullName = '';
      setTempState(prev => ({ ...prev, fullName: '' }));
    } else if (filter.startsWith('Onboard Date From:')) {
      newFilterState.onboardDateFrom = '';
      setTempState(prev => ({ ...prev, onboardDateFrom: '' }));
    } else if (filter.startsWith('Onboard Date To:')) {
      newFilterState.onboardDateTo = '';
      setTempState(prev => ({ ...prev, onboardDateTo: '' }));
    } else if (filter.startsWith('Email Sent From:')) {
      newFilterState.emailSentDateFrom = '';
      setTempState(prev => ({ ...prev, emailSentDateFrom: '' }));
    } else if (filter.startsWith('Email Sent To:')) {
      newFilterState.emailSentDateTo = '';
      setTempState(prev => ({ ...prev, emailSentDateTo: '' }));
    }
    
    newFilterState.activeFilters = newFilterState.activeFilters.filter(item => item !== filter);
    setFilterState(newFilterState);
  };
  
  useEffect(() => {
    setTempState({
      username: filterState.username,
      fullName: filterState.fullName,
      onboardDateFrom: filterState.onboardDateFrom,
      onboardDateTo: filterState.onboardDateTo,
      emailSentDateFrom: filterState.emailSentDateFrom,
      emailSentDateTo: filterState.emailSentDateTo
    });
  }, [filterState]);

  return {
    filterState,
    tempState,
    filterDrawerOpen,
    expandedAccordions,
    setFilterDrawerOpen,
    setExpandedAccordions,
    handleUsernameChange,
    handleFullNameChange,
    handleOnboardDateFromChange,
    handleOnboardDateToChange,
    handleEmailSentDateFromChange,
    handleEmailSentDateToChange,
    applyFilters,
    clearFilters,
    removeFilter
  };
};

export default useOnboardingFilter;
